<template>
  <div class="ai-analysis-loading">
    <div class="ai-analysis-loading__grid"></div>
    <div class="ai-analysis-loading__content">
      <div class="ai-analysis-loading__card">
        <div
          v-for="(item, index) in mockData"
          :key="item.id"
          class="ai-analysis-loading__step"
          :class="stepItemClassMap[item.type]"
        >
          <div class="ai-analysis-loading__track">
            <div class="ai-analysis-loading__status" :class="stepStatusClassMap[item.type]">
              <i
                v-if="item.type === 1"
                class="ai-analysis-loading__success-icon icon-checkbox-circle-line t-iconfont"
              ></i>
              <template v-else>{{ index + 1 }}</template>
            </div>
            <div v-if="item.type === 2" class="ai-analysis-loading__line"></div>
          </div>
          <div class="ai-analysis-loading__main">
            <div
              class="ai-analysis-loading__title"
              :class="{ 'ai-analysis-loading__title--secondary': item.type === 1 }"
            >
              {{ item.title }}
            </div>

            <clueCardLoading
              v-if="item.type === 2 && item.desc"
              :text="item.desc"
            ></clueCardLoading>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import clueCardLoading from '@/components/card-loading.vue'
import { ref } from 'vue'

const mockData = ref([
  {
    id: '1',
    title: '获取相关基础信息与历史跟进数据',
    desc: '正在获取数据...',
    type: 3 // 1: 完成, 2: 进行中, 3: 待开始
  },
  {
    id: '2',
    title: '梳理分析思路',
    desc: '正在梳理分析...',
    type: 3 // 1: 完成, 2: 进行中, 3: 待开始
  },
  {
    id: '3',
    title: '生成分析报告',
    desc: '正在生成报告...',
    type: 3 // 1: 完成, 2: 进行中, 3: 待开始
  }
])

const stepItemClassMap = {
  1: 'ai-analysis-loading__step--finished',
  2: 'ai-analysis-loading__step--active',
  3: 'ai-analysis-loading__step--pending'
}

const stepStatusClassMap = {
  1: 'ai-analysis-loading__status--success',
  2: 'ai-analysis-loading__status--active',
  3: 'ai-analysis-loading__status--pending'
}
defineExpose({
  mockData
})
</script>

<style lang="scss" scoped>
.ai-analysis-loading {
  --grid-size: 16px;
  --grid-color: rgba(220, 225, 235, 0.6);
  --bg-color: #f8faff;
  --violet-bg: #f5e8ff;
  --violet-color: #7a52f4;
  --success-color: #00c241;
  --line-light: #f2f3f5;
  --line-normal: #e5e6eb;
  --text-main: #323233;
  --text-secondary: #969799;
  --fill-light: #f7f8fa;

  position: relative;
  flex: 0 0 auto;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 24px;
  background-color: var(--bg-color);
  border: 1px solid #eef0f5;
  border-radius: 24px;
  user-select: none;

  &::after {
    position: absolute;
    inset: 0;
    z-index: 2;
    content: '';
    background: radial-gradient(circle at center, transparent 20%, var(--bg-color) 95%);
    pointer-events: none;
  }

  &__grid {
    position: absolute;
    inset: 0;
    z-index: 1;
    background-image:
      linear-gradient(var(--grid-color) 1px, transparent 1px),
      linear-gradient(90deg, var(--grid-color) 1px, transparent 1px);
    background-size: var(--grid-size) var(--grid-size);
    animation: moveGridUp 1s linear infinite;
    pointer-events: none;
  }

  &__content {
    position: relative;
    z-index: 3;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__card {
    width: 428px;
    padding: 12px;
    background: rgba(255, 255, 255, 0.72);
    border: 1px solid var(--violet-bg);
    border-radius: 16px;
    backdrop-filter: blur(6px);
  }

  &__step {
    display: flex;
    gap: 8px;

    & + & {
      margin-top: 12px;
    }
  }

  &__track {
    width: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
  }

  &__status {
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    border-radius: 50%;
    font-size: 12px;
    line-height: 16px;

    &--success {
      color: var(--success-color);
    }

    &--active {
      font-weight: 600;
      color: var(--violet-color);
      background: var(--violet-bg);
      border: 1px solid var(--violet-color);
    }

    &--pending {
      font-weight: 600;
      color: var(--text-secondary);
      background: var(--fill-light);
      border: 1px solid var(--line-normal);
    }
  }

  &__success-icon {
    font-size: 20px;
  }

  &__line {
    width: 1px;
    flex: 1;
    min-height: 8px;
    margin-top: 8px;
    background: var(--line-light);
  }

  &__main {
    flex: 1;

    :deep(.clue-card-loading) {
      margin-top: 8px;
      margin-bottom: 0;
    }
  }

  &__title {
    min-height: 22px;
    color: var(--text-main);
    font-size: 16px;
    line-height: 22px;

    &--secondary {
      color: var(--text-secondary);
    }
  }
}

@keyframes moveGridUp {
  0% {
    background-position: 0 0;
  }

  100% {
    background-position: 0 calc(-1 * var(--grid-size));
  }
}
</style>
